import { ComponentProps, PropsWithChildren, ReactNode } from 'react';

import classNames from 'classnames';

import {
  type Button,
  EmptyState,
  EmptyStateText,
  EmptyStateTitle,
  Flex,
  IconSearch,
  Link,
  Text,
} from '@spotify-internal/encore-web';
import { Box } from '@spotify-internal/encore-web/next';

import styles from './ListEmptyState.module.css';

export const EMPTY_STATE_ACTION_PROPS = {
  variant: 'tertiary',
  semanticColor: 'textBrightAccent',
  isCondensedInline: true,
  hasNoInteractiveBackground: true,
  size: 'medium',
} satisfies ComponentProps<typeof Button>;

export type ListEmptyStateProps = PropsWithChildren<{
  className?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  supportSnippet?: ReactNode;
  minimal?: boolean;
  icon?: ComponentProps<typeof EmptyState>['icon'];
  iconColorSet?: ComponentProps<typeof EmptyState>['iconColorSet'];
  illustration?: ReactNode;
}>;

export const ListEmptyState = ({
  className,
  title,
  description,
  actions,
  minimal,
  supportSnippet = (
    <span>
      Not the results you expected?{' '}
      <Link href="mailto:confidence@spotify.com" target="_blank" data-track-source-anchor="e5a1b8e078">
        Contact support
      </Link>
      .
    </span>
  ),
  icon: Icon = IconSearch,
  iconColorSet = 'announcement',
  illustration,
  children,
}: ListEmptyStateProps): JSX.Element => {
  if (minimal) {
    return (
      <Box className={classNames(styles.container, className)}>
        <Flex direction="column" alignItems="center" padding="base">
          <Text as="p" variant="bodyMedium">
            {title}
          </Text>
          {description && (
            <Text as="div" variant="bodySmall">
              {description}
            </Text>
          )}
          {actions}
        </Flex>
        {children}
      </Box>
    );
  }

  return (
    <Box className={classNames(styles.container, className)}>
      <EmptyState
        variant="contextual"
        maxInlineSize="600px"
        icon={!illustration ? Icon : undefined}
        iconColorSet={iconColorSet}
      >
        {illustration && (
          <Flex justifyContent="center" paddingBlockEnd="base">
            {illustration}
          </Flex>
        )}
        <EmptyStateTitle as="h4">{title}</EmptyStateTitle>
        {description && (
          <EmptyStateText as="div" paddingBottom="base">
            {description}
          </EmptyStateText>
        )}
        {actions && (
          <Flex
            alignItems="center"
            justifyContent="center"
            gap="tighter-2"
            wrap="wrap"
          >
            {actions}
          </Flex>
        )}
        {Boolean(supportSnippet) && (
          <Text
            as="div"
            variant="marginal"
            semanticColor="textSubdued"
            className={styles.support}
          >
            {supportSnippet}
          </Text>
        )}
      </EmptyState>
      {children}
    </Box>
  );
};
